import React, { useEffect, useRef } from "react";

import { noop } from "lodash-es";
import mergeRefs from "merge-refs";
import { twJoin, twMerge } from "tailwind-merge";

export const CHECKBOX_WRAPPER_CLASS = "tktl-checkbox-wrapper" as const;

const HIT_AREA_CLASSES = twJoin(
  "cursor-pointer",
  "after:absolute after:top-1/2 after:left-1/2 after:content-['']",
  "after:h-8 after:w-8 after:-translate-x-1/2 after:-translate-y-1/2",
);

type InputProps = React.HTMLProps<HTMLInputElement> & {
  dataLoc?: string;
  /**
   * Indeterminate state takes visual precedence over the checked state
   * This means that if both `checked` and `indeterminate` are true,
   * the checkbox will be in indeterminate state.
   */
  indeterminate?: boolean;
};

export const Checkbox: React.FC<InputProps> = ({
  id,
  checked,
  onChange,
  className,
  dataLoc,
  indeterminate = false,
  ref,
  disabled,
  readOnly,
  ...props
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!checkboxRef.current) return;
    checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={twMerge(
        CHECKBOX_WRAPPER_CLASS,
        "relative isolate inline-flex h-3 w-3",
        !disabled && !readOnly && HIT_AREA_CLASSES,
        className,
      )}
    >
      <input
        ref={mergeRefs(ref, checkboxRef)}
        checked={checked}
        disabled={disabled}
        readOnly={readOnly}
        className={twJoin(
          "relative z-10 h-3 w-3 rounded-sm border border-gray-400 text-gray-0 accent-indigo-600",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
        data-loc={dataLoc}
        id={id}
        type="checkbox"
        onChange={readOnly ? noop : onChange}
        {...props}
      />
    </label>
  );
};
